import {
  ReferencesInlineStyle,
  StylePrefix,
  getInlineStylesValue,
} from '@msd-npm-gmc-dev/gmc-dc-assembler-draftjs2lexical';
import { BrandColorRef, getBrandFontValueFontFamily } from 'modules/BrandDefinition';
import { FontPluginStyle } from 'modules/Lexical/Plugins/FontPlugin/style';
import * as Constants from 'const';
import * as Models from 'models';
import { toPx } from 'utils/toPx';

enum ConvertStylesEnum {
  UNDERLINE = 'UNDERLINE',
  SUB = 'SUBSCRIPT',
  SUP = 'SUPERSCRIPT',
}

enum LexicalFormatCodeEnum {
  BOLD = 1,
  ITALIC = 2,
  UNDERLINE = 8,
  DEFAULT = 0,
  SUB = 32,
  SUP = 64,
}

function isHex(value: string): boolean {
  return /^[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8}$/.test(value.toLowerCase());
}

export function getFormateCode(styles: string[]): number {
  return styles.reduce((acc, curr) => {
    const isUnderline = curr.includes(ConvertStylesEnum.UNDERLINE) ? LexicalFormatCodeEnum.UNDERLINE : LexicalFormatCodeEnum.DEFAULT;
    const isSub = curr.includes(ConvertStylesEnum.SUB) ? LexicalFormatCodeEnum.SUB : LexicalFormatCodeEnum.DEFAULT;
    const isSup = curr.includes(ConvertStylesEnum.SUP) ? LexicalFormatCodeEnum.SUP : LexicalFormatCodeEnum.DEFAULT;

    return acc + isUnderline + isSup + isSub;
  }, 0);
}

function getFixedBrandColorRef(
  colorHexOrName: string | undefined,
  name: string | undefined,
): BrandColorRef | undefined {
  if (colorHexOrName) {
    if (isHex(colorHexOrName)) {
      return { HEX: colorHexOrName, name };
    }

    return {
      HEX: Constants.DefaultCustomStyle.FONT_COLOR as string,
      name: name ?? colorHexOrName,
    };
  }
  if (name) {
    return { HEX: Constants.DefaultCustomStyle.FONT_COLOR as string, name };
  }

  return undefined;
}

function getFontStyle(styles: string[]): string {
  const value = getInlineStylesValue(styles, StylePrefix.FONT_STYLE);
  if (value && value !== 'undefined') {
    return value;
  }

  return styles.includes(ReferencesInlineStyle.ITALIC)
    ? Constants.CharStyleValue.ITALIC
    : Constants.CharStyleValue.NORMAL;
}

function getFontWight(styles: string[]): string {
  const value = getInlineStylesValue(styles, StylePrefix.FONT_WEIGHT);
  if (value && value !== 'undefined') {
    return value;
  }

  return styles.includes(ReferencesInlineStyle.BOLD)
    ? Constants.CharStyleValue.BOLD
    : Constants.CharStyleValue.REGULAR;
}

export function convertTextStyles(
  styles: string[],
  fonts?: Models.BrandFontsList,
): string {
  const result: string[] = [];

  // draftjs may provide wrong values for the color name and color hex value
  const brandColor = getFixedBrandColorRef(
    getInlineStylesValue(styles, StylePrefix.FONT_COLOR),
    getInlineStylesValue(styles, StylePrefix.FONT_COLOR_NAME),
  );
  if (brandColor) {
    result.push(`${FontPluginStyle.BRAND_COLOR_HEX}: ${brandColor.HEX}`);
    if (brandColor.name) {
      result.push(`${FontPluginStyle.BRAND_COLOR_NAME}: ${brandColor.name}`);
    }
    const barndColorTint = getInlineStylesValue(styles, StylePrefix.FONT_COLOR_TINT, 'number');
    if (barndColorTint) {
      result.push(`${FontPluginStyle.BRAND_COLOR_TINT}: ${barndColorTint}`);
    }
    result.push(`${FontPluginStyle.COLOR}: ${brandColor.HEX}`);
  }

  const brandFontName = getInlineStylesValue(styles, StylePrefix.FONT_FAMILY);
  if (brandFontName) {
    result.push(`${FontPluginStyle.BRAND_FONT_NAME}: ${brandFontName}`);
    const characterStyleName = getInlineStylesValue(styles, StylePrefix.CHARACTER_STYLE_NAME);
    if (characterStyleName && characterStyleName !== 'undefined') {
      result.push(`${FontPluginStyle.BRAND_FONT_CHARACTER_STYLE_NAME}: ${characterStyleName}`);
    }
    if (fonts) {
      const font = getBrandFontValueFontFamily(fonts, { fontName: brandFontName, characterStyleName });
      result.push(`${FontPluginStyle.FONT_FAMILY}: ${font}`);
    }
  }

  const fontSize = getInlineStylesValue(styles, StylePrefix.FONT_SIZE);
  const fontSizePx = fontSize && toPx(fontSize);
  if (fontSizePx) {
    result.push(`${FontPluginStyle.FONT_SIZE}: ${fontSizePx}`);
  }

  const fontStyle = getFontStyle(styles);
  result.push(`${FontPluginStyle.FONT_STYLE}: ${fontStyle}`);

  const fontWeight = getFontWight(styles);
  result.push(`${FontPluginStyle.FONT_WEIGHT}: ${fontWeight}`);

  return result.join('; ').concat(';');
}
